import { useState, useEffect } from 'react';
import { useWallet } from '../../context/WalletContext';
import { useDolphin, getExplorerUrl } from '../../hooks/useDolphin';
import ConnectPrompt from '../../components/common/ConnectPrompt';
import toast from 'react-hot-toast';
import { ethers } from 'ethers';
import { 
  FaWallet, 
  FaCopy, 
  FaCheckCircle,
  FaPaperPlane,
  FaArrowDown,
 
  FaHistory,
  FaShieldAlt,
  FaCoins,
  FaExchangeAlt,
  FaLock,
  FaUnlock,
  FaExternalLinkAlt,
  FaTimes,
  FaCheck,
  FaInfoCircle,
  FaNetworkWired,
  FaSpinner
} from 'react-icons/fa';
import { HiOutlineQrcode } from 'react-icons/hi';
import QRCode from 'react-qr-code';

const Wallet = ({ initialSection = 'overview' }) => {

  const { isConnected, account, provider } = useWallet();
  const { 
    stats, 
    userStats, 
    transactionHistory, 
    historyLoading,
    sendTokens,
    estimateGas,
    refreshTransactionHistory: refreshTransactions,
    // dolphinContract, // removed unused variable
    
  } = useDolphin();
  // Referral modal logic
  // Removed references to setInitialInviteCode and setShowReferralModal

  

  const [activeSection, setActiveSection] = useState(initialSection);
  const [copied, setCopied] = useState(false);
  const [nativeBalance, setNativeBalance] = useState('0');
  const [showQRModal, setShowQRModal] = useState(false);
  
  // Send form state
  const [sendAddress, setSendAddress] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  // const [sendType, setSendType] = useState('external'); // Only external is real on-chain
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [gasEstimate, setGasEstimate] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Transaction filter
  const [txFilter, setTxFilter] = useState('all');
  
  // Fetch native balance with reduced polling and error feedback
  useEffect(() => {
    const fetchBalance = async () => {
      if (provider && account) {
        try {
          const balance = await provider.getBalance(account);
          setNativeBalance(ethers.utils.formatEther(balance));
        } catch (err) {
          if (err && err.message && (err.message.includes('rate limit') || err.message.includes('429'))) {
            toast.error('Network is busy or rate-limited. Please try again later or switch RPC provider.');
          }
          console.error('Error fetching balance:', err);
        }
      }
    };
    fetchBalance();
    // Refresh every 60 seconds
    const interval = setInterval(fetchBalance, 60000);
    return () => clearInterval(interval);
  }, [provider, account]);

  // Estimate gas when send form changes
  useEffect(() => {
    const getGas = async () => {
      if (sendAddress && sendAmount && parseFloat(sendAmount) > 0 && ethers.utils.isAddress(sendAddress)) {
        const estimate = await estimateGas(sendAddress, sendAmount);
        setGasEstimate(estimate);
      } else {
        setGasEstimate(null);
      }
    };
    getGas();
  }, [sendAddress, sendAmount, estimateGas]);

  if (!isConnected && localStorage.getItem('walletPromptSkipped') !== 'true') {
    return <ConnectPrompt />;
  }

  const copyAddress = () => {
    navigator.clipboard.writeText(account);
    setCopied(true);
    toast.success('Address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shortenAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 8)}...${addr.slice(-6)}`;
  };

  // Calculate wallet values from real blockchain data
  const dolpBalance = parseFloat(userStats.vested) || 0;
  const dolpPrice = parseFloat(stats.currentPrice) || 1;
  const dolpValue = dolpBalance * dolpPrice;
  const lockedBalance = dolpBalance; // All vested tokens are locked until claim
  const availableBalance = parseFloat(nativeBalance) || 0; // POL is available for sending
  const claimableBalance = parseFloat(userStats.claimable) || 0;
  // const pendingUSDT = parseFloat(userStats.pendingBalance) || 0; // removed unused variable



  const handleSend = () => {
    if (!sendAddress || !sendAmount) {
      toast.error('Please fill in all fields');
      return;
    }
    if (!ethers.utils.isAddress(sendAddress)) {
      toast.error('Invalid recipient address');
      return;
    }
    if (parseFloat(sendAmount) <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (parseFloat(sendAmount) > availableBalance) {
      toast.error('Insufficient POL balance');
      return;
    }
    setShowSendConfirm(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      const result = await sendTokens(sendAddress, sendAmount);
      toast.success(
        <div>
          Transaction confirmed!
          <a 
            href={getExplorerUrl(result.tx.hash)} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ marginLeft: '8px', color: '#a78bfa' }}
          >
            View →
          </a>
        </div>
      );
      setShowSendConfirm(false);
      setSendAddress('');
      setSendAmount('');
      setGasEstimate(null);
      // Refresh balance
      const balance = await provider.getBalance(account);
      setNativeBalance(ethers.utils.formatEther(balance));
    } catch (err) {
      console.error('Send failed:', err);
      toast.error(err.message || 'Transaction failed');
    } finally {
      setSending(false);
    }
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: <FaWallet /> },
    { id: 'send', label: 'Send', icon: <FaPaperPlane /> },
    { id: 'receive', label: 'Receive', icon: <FaArrowDown /> },
    { id: 'history', label: 'History', icon: <FaHistory /> },
    { id: 'security', label: 'Security', icon: <FaShieldAlt /> },
  ];

  // const getTransactionIcon = (type) => {
  //   switch(type) {
  //     case 'send': return <FaPaperPlane className="tx-icon send" />;
  //     case 'receive': return <FaArrowDown className="tx-icon receive" />;
  //     case 'mining': return <FaHammer className="tx-icon mining" />;
  //     case 'level': return <FaCoins className="tx-icon level" />;
  //     default: return <FaExchangeAlt className="tx-icon" />;
  //   }
  // };

  // Filter contract history events
  const filteredHistory = (transactionHistory || []).filter(item => {
    if (txFilter === 'all') return true;
    // You can expand this filter logic to group by type if needed
    if (txFilter === 'mining') return item.type === 'MINING';
    if (txFilter === 'level') return item.type === 'LEVEL_PURCHASE';
    if (txFilter === 'invite') return item.type === 'INVITE_GENERATED';
    // Add more filters as needed
    return false;
  });

    return (
      <div className="wallet-container fade-in bg-gradient-to-br from-white to-gray-100 dark:from-gray-950 dark:to-gray-900 min-h-screen p-2 sm:p-4 lg:p-6 rounded-2xl">
      {/* Wallet Header */}
      <div className="wallet-header flex flex-col sm:flex-row items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FaWallet className="text-cyan-500 text-lg" />
          <h1 className="text-lg font-bold text-cyan-700 dark:text-cyan-200">My Wallet</h1>
          <span className="ml-2 px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs dark:bg-emerald-900 dark:text-emerald-200">Active</span>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="wallet-quick-actions grid grid-cols-5 gap-1 sm:flex sm:gap-2 mb-2 w-full">
        {sections.map(section => (
          <button
            key={section.id}
            className={`flex flex-col items-center justify-center px-1.5 py-1 sm:px-2 sm:py-2 rounded-md border font-medium transition-all
              text-[11px] sm:text-base
              w-full
              ${activeSection === section.id
                ? 'bg-cyan-100 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-200 border-cyan-300 dark:border-cyan-800'
                : 'bg-white/80 dark:bg-gray-900/80 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-800 hover:bg-cyan-50 dark:hover:bg-cyan-900/40'}
            `}
            style={{ minWidth: 0 }}
            onClick={() => setActiveSection(section.id)}
          >
            <span className="mb-0.5 text-base sm:text-xl">{section.icon}</span>
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      {/* Main Content */}
      <div className="wallet-content">
        
        {/* OVERVIEW SECTION */}
        {activeSection === 'overview' && (
          <div className="wallet-overview fade-in space-y-3">
            {/* Balance Card - Responsive, prominent on web */}
            <div className="balance-card bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-900 shadow rounded-lg px-1 py-1 sm:px-2 sm:py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-1 transition-all duration-200 w-full max-w-full overflow-x-auto">
              <div className="flex flex-col gap-0.5 sm:gap-1">
                <span className="balance-label text-[9px] sm:text-xs text-emerald-700 dark:text-emerald-200 font-medium">Total Balance</span>
                <span className="balance-amount text-base sm:text-xl font-extrabold text-emerald-700 dark:text-emerald-200 tracking-tight">{dolpBalance.toLocaleString()}</span>
                <span className="balance-token text-[7px] sm:text-xs text-emerald-400 dark:text-emerald-300 font-semibold">DOLP</span>
                <span className="balance-usd text-[9px] sm:text-xs text-emerald-500 dark:text-emerald-300 font-medium">≈ ${dolpValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD</span>
              </div>
              <div className="flex flex-col items-end gap-0.5 sm:gap-1">
                <button className="qr-btn text-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-300 bg-emerald-100 dark:bg-emerald-900 p-0.5 sm:p-1 rounded-lg shadow-sm" onClick={() => setShowQRModal(true)}>
                  <HiOutlineQrcode size={12} className="sm:hidden" />
                  <HiOutlineQrcode size={18} className="hidden sm:block" />
                </button>
                <button className="flex items-center gap-0.5 sm:gap-1 text-[8px] sm:text-xs text-emerald-700 dark:text-emerald-200 font-medium bg-emerald-100 dark:bg-emerald-900 px-1 sm:px-2 py-0.5 sm:py-0.5 rounded-md shadow-sm" onClick={copyAddress}>
                  {shortenAddress(account)}
                  {copied ? <FaCheck className="ml-1 text-emerald-500" /> : <FaCopy className="ml-1" />}
                </button>
              </div>
            </div>

            {/* Downline & Payout Analytics removed as requested */}
            {/* Balance Breakdown */}
            <div className="balance-breakdown bg-white/80 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 shadow-xs rounded-md p-0.5 sm:p-1 mt-2 flex flex-col gap-1 sm:gap-2 w-full max-w-full overflow-x-auto">
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="p-1 sm:p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded text-emerald-600 dark:text-emerald-200"><FaUnlock className="text-xs sm:text-lg" /></span>
                <span className="text-xs sm:text-base text-gray-500">Claimable</span>
                <span className="ml-auto font-bold text-xs sm:text-base text-cyan-700 dark:text-cyan-200">{claimableBalance.toLocaleString()} DOLP</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="p-1 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded text-purple-600 dark:text-purple-200"><FaLock className="text-xs sm:text-lg" /></span>
                <span className="text-xs sm:text-base text-gray-500">Locked (Vesting)</span>
                <span className="ml-auto font-bold text-xs sm:text-base text-purple-700 dark:text-purple-200">{lockedBalance.toLocaleString()} DOLP</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="p-1 sm:p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded text-cyan-600 dark:text-cyan-200"><FaCoins className="text-xs sm:text-lg" /></span>
                <span className="text-xs sm:text-base text-gray-500">POL Balance</span>
                <span className="ml-auto font-bold text-xs sm:text-base text-cyan-700 dark:text-cyan-200">{parseFloat(nativeBalance).toFixed(4)} POL</span>
              </div>
            </div>

            {/* Assets List */}
            <div className="assets-card bg-white/80 dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 shadow-xs rounded-md p-0.5 sm:p-1 mt-2 w-full max-w-full overflow-x-auto">
              <div className="assets-header mb-0.5 sm:mb-1">
                <h3 className="text-[7px] sm:text-xs font-semibold text-gray-700 dark:text-gray-200">Assets</h3>
              </div>
              <div className="assets-list flex flex-col gap-0.5 sm:gap-1">
                <div className="asset-item flex items-center gap-0.5 sm:gap-1">
                  <div className="asset-icon dolp text-[9px] sm:text-xs">🐬</div>
                  <div className="asset-info flex flex-col">
                    <span className="asset-name text-[7px] sm:text-xs text-gray-500">DOLPCOIN</span>
                    <span className="asset-symbol text-[7px] sm:text-xs text-gray-400">DOLP</span>
                  </div>
                  <div className="asset-balance ml-auto flex flex-col items-end">
                    <span className="asset-amount text-[7px] sm:text-xs font-bold text-cyan-700 dark:text-cyan-200">{dolpBalance.toLocaleString()}</span>
                    <span className="asset-usd text-[7px] sm:text-xs text-gray-400">${dolpValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
                <div className="asset-item flex items-center gap-0.5 sm:gap-1">
                  <div className="asset-icon pol">
                    <img src="https://cryptologos.cc/logos/polygon-matic-logo.png" alt="POL" className="w-2 h-2 sm:w-4 sm:h-4" />
                  </div>
                  <div className="asset-info flex flex-col">
                    <span className="asset-name text-[7px] sm:text-xs text-gray-500">Polygon</span>
                    <span className="asset-symbol text-[7px] sm:text-xs text-gray-400">POL</span>
                  </div>
                  <div className="asset-balance ml-auto flex flex-col items-end">
                    <span className="asset-amount text-[7px] sm:text-xs font-bold text-cyan-700 dark:text-cyan-200">{parseFloat(nativeBalance).toFixed(4)}</span>
                    <span className="asset-usd text-[7px] sm:text-xs text-gray-400">~${(parseFloat(nativeBalance) * 0.5).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats removed for minimal UI */}

            {/* UI improvement: add more space and separation between cards for clarity */}
            <div className="h-2 sm:h-4" />
          </div>
        )}

        {/* SEND SECTION */}
        {activeSection === 'send' && (
          <div className="wallet-send fade-in bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 shadow rounded-lg p-2 sm:p-4">
            <div className="section-card text-xs sm:text-sm">
              <h3 className="section-title text-xs sm:text-base mb-2 flex items-center gap-1">
                <FaPaperPlane className="text-sm sm:text-base" /> Send POL
              </h3>
              <div className="info-box warning mb-2">
                <div className="toast-message warning flex items-center gap-1">
                  <FaInfoCircle className="text-xs sm:text-sm" />
                  <span>
                    DOLP tokens are vested and cannot be transferred until claimed.<br />
                    You can send POL (native token) for transactions.
                  </span>
                </div>
              </div>
              <div className="send-form space-y-2">
                <div className="form-group">
                  <label className="form-label text-xs sm:text-sm">Recipient Address</label>
                  <input
                    type="text"
                    className="form-input text-xs sm:text-sm py-1 px-2"
                    placeholder="Enter wallet address (0x...)"
                    value={sendAddress}
                    onChange={(e) => setSendAddress(e.target.value)}
                  />
                  {sendAddress && !ethers.utils.isAddress(sendAddress) && (
                    <span className="input-error text-xs"><FaTimes /> Invalid address format</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label text-xs sm:text-sm">Amount</label>
                  <div className="amount-input-wrapper flex items-center gap-1">
                    <input
                      type="number"
                      className="form-input text-xs sm:text-sm py-1 px-2"
                      placeholder="0.00"
                      value={sendAmount}
                      onChange={(e) => setSendAmount(e.target.value)}
                      step="0.0001"
                      min="0"
                    />
                    <button className="max-btn text-xs px-1 py-0.5" onClick={() => setSendAmount((availableBalance - 0.01).toFixed(4))}>
                      MAX
                    </button>
                    <span className="token-badge text-xs">POL</span>
                  </div>
                  <div className="input-helper text-[10px]">Available: {availableBalance.toFixed(4)} POL</div>
                </div>
                {gasEstimate && (
                  <div className="fee-estimate text-[10px]">
                    <div className="fee-row flex justify-between"><span>Network Fee</span><span>~{parseFloat(gasEstimate.gasCostPOL).toFixed(6)} POL</span></div>
                    <div className="fee-row flex justify-between"><span>Gas Price</span><span>{parseFloat(gasEstimate.gasPrice).toFixed(2)} Gwei</span></div>
                    <div className="fee-row flex justify-between"><span>Estimated Time</span><span>~2 seconds</span></div>
                  </div>
                )}
                <button 
                  className="btn btn-primary btn-sm btn-full text-xs py-1 mt-1"
                  onClick={handleSend}
                  disabled={!sendAddress || !sendAmount || parseFloat(sendAmount) <= 0 || !ethers.utils.isAddress(sendAddress)}
                >
                  <FaPaperPlane className="text-xs" /> Review Transfer
                </button>
              </div>
              <div className="info-box info mt-2">
                <FaInfoCircle className="text-xs sm:text-sm" />
                <p className="text-xs sm:text-sm">
                  Transfers are on-chain and require POL for gas fees. 
                  Transaction will be confirmed on Polygon network.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* RECEIVE SECTION */}
        {activeSection === 'receive' && (
          <div className="wallet-receive fade-in bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 shadow rounded-lg p-2 sm:p-4">
            <div className="section-card text-xs sm:text-sm">
              <h3 className="section-title text-xs sm:text-base mb-2 flex items-center gap-1">
                <FaArrowDown className="text-sm sm:text-base" /> Receive DOLPCOIN
              </h3>
              <div className="receive-content space-y-2">
                <div className="qr-display flex justify-center">
                  <div className="qr-wrapper">
                    <QRCode 
                      value={account || ''} 
                      size={100}
                      bgColor="#1a1225"
                      fgColor="#ffffff"
                    />
                  </div>
                </div>
                <div className="address-display">
                  <label className="form-label text-xs sm:text-sm">Your Wallet Address</label>
                  <div className="address-box text-xs sm:text-sm" onClick={copyAddress}>
                    <span className="address-full break-all">{account}</span>
                    <button className="copy-btn text-xs sm:text-sm">
                      {copied ? <FaCheck /> : <FaCopy />}
                    </button>
                  </div>
                </div>
                <div className="network-warning flex items-center gap-1 text-xs sm:text-sm">
                  <FaInfoCircle />
                  <div>
                    <strong>Important: Polygon Network Only</strong>
                    <p>Only send DOLPCOIN on Polygon network. Sending from other networks will result in permanent loss.</p>
                  </div>
                </div>
                <div className="deposit-info text-[10px] sm:text-xs">
                  <div className="deposit-row flex justify-between"><span>Network</span><span className="network-badge">Polygon Mainnet</span></div>
                  <div className="deposit-row flex justify-between"><span>Minimum Deposit</span><span>1 DOLP</span></div>
                  <div className="deposit-row flex justify-between"><span>Confirmations</span><span>12 blocks (~24 sec)</span></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MINING SECTION REMOVED FOR USERS */}

        {/* HISTORY SECTION */}
        {activeSection === 'history' && (
          <div className="wallet-history fade-in bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 shadow rounded-lg p-2 sm:p-4">
            <div className="section-card text-xs sm:text-sm">
              <h3 className="section-title text-xs sm:text-base mb-2 flex items-center gap-1">
                <FaHistory className="text-sm sm:text-base" /> Transaction History
                <button 
                  className="refresh-btn ml-2 text-xs sm:text-sm" 
                  onClick={refreshTransactions}
                  title="Refresh transactions"
                  disabled={historyLoading}
                >
                  <FaExchangeAlt />
                </button>
              </h3>
              <div className="history-filters flex gap-1 mb-2">
                <button 
                  className={`filter-btn text-[10px] sm:text-xs ${txFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setTxFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-btn text-[10px] sm:text-xs ${txFilter === 'mining' ? 'active' : ''}`}
                  onClick={() => setTxFilter('mining')}
                >
                  Mining
                </button>
                <button 
                  className={`filter-btn text-[10px] sm:text-xs ${txFilter === 'level' ? 'active' : ''}`}
                  onClick={() => setTxFilter('level')}
                >
                  Level Purchases
                </button>
                <button 
                  className={`filter-btn text-[10px] sm:text-xs ${txFilter === 'invite' ? 'active' : ''}`}
                  onClick={() => setTxFilter('invite')}
                >
                  Invite Codes
                </button>
              </div>
              <div className="transactions-list space-y-1">
                {historyLoading ? (
                  <div className="empty-state text-xs">Loading history...</div>
                ) : filteredHistory.length > 0 ? (
                  filteredHistory.map((item, idx) => (
                    <div key={item.transactionHash || idx} className="transaction-item flex flex-col sm:flex-row sm:items-center justify-between p-1 sm:p-2 bg-gray-900/30 rounded-md text-xs sm:text-sm">
                      <div className="tx-left flex items-center gap-2">
                        <span className="tx-icon text-base sm:text-lg">{item.icon}</span>
                        <div className="tx-info">
                          <span className="tx-type font-semibold">{item.title}</span>
                          <span className="tx-date block text-[10px] text-gray-400">{item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}</span>
                          <span className="tx-desc block text-[10px] text-gray-500">{item.description}</span>
                        </div>
                      </div>
                      <div className="tx-right flex flex-col items-end gap-1">
                        {item.amount !== null && (
                          <span className="tx-amount font-semibold">{item.amount}</span>
                        )}
                        <div className="tx-status-row flex items-center gap-1">
                          <span className={`tx-status text-[10px] ${item.status}`}>{item.status}</span>
                          {item.transactionHash && (
                            <a 
                              href={getExplorerUrl(item.transactionHash)} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="tx-link"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state text-xs">
                    <FaHistory style={{ fontSize: '2rem', opacity: 0.3 }} />
                    <p>
                      {txFilter === 'all' 
                        ? 'No transactions yet. Start mining or invite someone to see your activity!' 
                        : `No ${txFilter} transactions found.`}
                    </p>
                  </div>
                )}
              </div>
              <div className="info-box info mt-2">
                <FaInfoCircle className="text-xs sm:text-sm" />
                <p className="text-xs sm:text-sm">
                  Transaction history is fetched from the Polygon blockchain. 
                  Only the last 10,000 blocks are scanned. Older transactions may not appear.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SECURITY SECTION */}
        {activeSection === 'security' && (
          <div className="wallet-security fade-in bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 shadow rounded-lg p-2 sm:p-4">
            <div className="section-card text-xs sm:text-sm">
              <h3 className="section-title text-xs sm:text-base mb-2 flex items-center gap-1">
                <FaShieldAlt className="text-sm sm:text-base" /> Security Settings
              </h3>
              <div className="security-info-banner flex items-center gap-2 mb-2">
                <FaShieldAlt className="security-banner-icon text-base sm:text-lg" />
                <div>
                  <strong className="text-xs sm:text-sm">Your Wallet is Secured by Blockchain</strong>
                  <p className="text-[10px] sm:text-xs">Your private keys never leave your wallet. All transactions require your signature.</p>
                </div>
              </div>
              <div className="security-options space-y-1">
                <div className="security-option flex items-center justify-between bg-gray-900/30 rounded-md p-1">
                  <div className="security-option-left flex items-center gap-2">
                    <div className="security-icon verified text-xs sm:text-sm">
                      <FaCheck />
                    </div>
                    <div className="security-info">
                      <span className="security-name text-xs sm:text-sm">Wallet Connected</span>
                      <span className="security-desc text-[10px] sm:text-xs">{shortenAddress(account)}</span>
                    </div>
                  </div>
                  <span className="security-badge verified text-[10px] sm:text-xs">
                    <FaCheckCircle /> Verified
                  </span>
                </div>
                <div className="security-option flex items-center justify-between bg-gray-900/30 rounded-md p-1">
                  <div className="security-option-left flex items-center gap-2">
                    <div className="security-icon verified text-xs sm:text-sm">
                      <FaNetworkWired />
                    </div>
                    <div className="security-info">
                      <span className="security-name text-xs sm:text-sm">Network</span>
                      <span className="security-desc text-[10px] sm:text-xs">Polygon Mainnet (Chain ID: 137)</span>
                    </div>
                  </div>
                  <span className="security-badge verified text-[10px] sm:text-xs">
                    <FaCheckCircle /> Connected
                  </span>
                </div>
                <div className="security-option flex items-center justify-between bg-gray-900/30 rounded-md p-1">
                  <div className="security-option-left flex items-center gap-2">
                    <div className="security-icon text-xs sm:text-sm">
                      <FaLock />
                    </div>
                    <div className="security-info">
                      <span className="security-name text-xs sm:text-sm">Transaction Signing</span>
                      <span className="security-desc text-[10px] sm:text-xs">All transactions require wallet approval</span>
                    </div>
                  </div>
                  <span className="security-badge verified text-[10px] sm:text-xs">
                    <FaCheckCircle /> Required
                  </span>
                </div>
                <div className="security-option flex items-center justify-between bg-gray-900/30 rounded-md p-1">
                  <div className="security-option-left flex items-center gap-2">
                    <div className="security-icon text-xs sm:text-sm">
                      <FaCoins />
                    </div>
                    <div className="security-info">
                      <span className="security-name text-xs sm:text-sm">Vesting Protection</span>
                      <span className="security-desc text-[10px] sm:text-xs">Tokens locked for {stats.vestingDays} days after mining ends</span>
                    </div>
                  </div>
                  <span className="security-badge info text-[10px] sm:text-xs">
                    {stats.miningEnded ? 'Active' : 'Pending'}
                  </span>
                </div>
              </div>
              <div className="security-actions mt-2">
                <a 
                  href={`https://polygonscan.com/address/${account}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline btn-full text-xs sm:text-sm"
                >
                  <FaExternalLinkAlt /> View on PolygonScan
                </a>
              </div>
              <div className="security-notice mt-2 flex items-center gap-1">
                <FaInfoCircle className="text-xs sm:text-sm" />
                <p className="text-[10px] sm:text-xs">
                  <strong>Never share your private keys or seed phrase.</strong> 
                  DOLPHIN team will never ask for your private information. 
                  All security is handled by your connected wallet (TokenPocket, etc.).
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* QR Modal */}
      {showQRModal && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content qr-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowQRModal(false)}>
              <FaTimes />
            </button>
            <div className="qr-modal-content">
              <div className="qr-wrapper large">
                <QRCode 
                  value={account || ''} 
                  size={250}
                  bgColor="#1a1225"
                  fgColor="#ffffff"
                />
              </div>
              <p className="qr-address">{account}</p>
              <button className="btn btn-primary" onClick={copyAddress}>
                <FaCopy /> Copy Address
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendConfirm && (
        <div className="modal-overlay" onClick={() => !sending && setShowSendConfirm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header confirm-header">
              <FaPaperPlane />
              <h2>Confirm Transfer</h2>
            </div>
            <div className="modal-body">
              <div className="confirm-details">
                <div className="confirm-row">
                  <span>To</span>
                  <span className="confirm-value">{shortenAddress(sendAddress)}</span>
                </div>
                <div className="confirm-row">
                  <span>Amount</span>
                  <span className="confirm-value">{sendAmount} POL</span>
                </div>
                {gasEstimate && (
                  <>
                    <div className="confirm-row">
                      <span>Network Fee</span>
                      <span className="confirm-value">~{parseFloat(gasEstimate.gasCostPOL).toFixed(6)} POL</span>
                    </div>
                    <div className="confirm-row highlight">
                      <span>Total</span>
                      <span className="confirm-value">
                        {(parseFloat(sendAmount) + parseFloat(gasEstimate.gasCostPOL)).toFixed(6)} POL
                      </span>
                    </div>
                  </>
                )}
              </div>
              
              <div className="info-box warning" style={{ marginTop: '1rem' }}>
                <div className="toast-message warning">
                  <FaInfoCircle />
                  <span>This transaction will be broadcast to the Polygon network. Please verify the recipient address carefully.</span>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn btn-outline" 
                  onClick={() => setShowSendConfirm(false)}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={confirmSend}
                  disabled={sending}
                >
                  {sending ? (
                    <>
                      <FaSpinner className="spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <FaCheck /> Confirm & Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Wallet;
